import React from "react";

/**
 * PaperTitleAbstract Component
 * 
 * Step 3 of the paper submission wizard.
 * Handles only title and abstract input with auto-completion from AI extraction.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data
 * @param {Function} props.handleChange - Form field change handler
 * @param {Object} props.extractedInfo - AI-extracted information
 * @param {boolean} props.hasExtractedInfo - Whether AI extraction was performed
 */
const PaperTitleAbstract = ({
  formData,
  handleChange,
  handleTitleBlur,
  extractedInfo,
  hasExtractedInfo
}) => {
  return (
    <div className="row">
      <div className="col-12">
        <h4 className="mb-4">
          <i className="bi bi-file-text me-2"></i>
          Paper Title and Abstract
        </h4>
        <p className="text-muted mb-4">
          Review and edit the title and abstract for your paper submission.
          {hasExtractedInfo && " Information has been automatically extracted from your document."}
        </p>

        {/* Required fields notice */}
        <div className="mb-3">
          <small className="text-muted">
            <span className="text-danger">*</span> Required
          </small>
        </div>

        {/* Title Input */}
        <div className="mb-4">
          <label htmlFor="manuscriptTitle" className="form-label fw-bold">
            Paper Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            id="manuscriptTitle"
            name="manuscriptTitle"
            className="form-control"
            value={formData.manuscriptTitle}
            onChange={handleChange}
            onBlur={handleTitleBlur}
            placeholder="Enter the title of your paper"
            required
          />
          {hasExtractedInfo && extractedInfo?.extractedInfo?.title && formData.manuscriptTitle !== extractedInfo.extractedInfo.title && (
            <div className="form-text">
              <strong>AI Suggestion:</strong> {extractedInfo.extractedInfo.title}
            </div>
          )}
        </div>

        {/* Abstract Input */}
        <div className="mb-4">
          <label htmlFor="manuscriptAbstract" className="form-label fw-bold">
            Abstract <span className="text-danger">*</span>
          </label>
          <textarea
            id="manuscriptAbstract"
            name="manuscriptAbstract"
            className="form-control"
            rows="8"
            value={formData.manuscriptAbstract}
            onChange={handleChange}
            placeholder="Enter the abstract of your paper"
            required
          />
          <div className="form-text">
            {Math.ceil(formData.manuscriptAbstract.split(/\s+/).filter(word => word.length > 0).length)} / 250 words maximum
          </div>
          {hasExtractedInfo && extractedInfo?.extractedInfo?.abstract && formData.manuscriptAbstract !== extractedInfo.extractedInfo.abstract && (
            <div className="mt-2">
              <div className="card">
                <div className="card-header bg-light py-2">
                  <h6 className="mb-0">
                    <i className="bi bi-lightbulb me-2"></i>
                    AI-Extracted Abstract
                  </h6>
                </div>
                <div className="card-body py-3">
                  <p className="mb-0 small">{extractedInfo.extractedInfo.abstract}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Information Box */}
        <div className="alert alert-secondary" role="alert">
          <p className="mb-0">
            <i className="bi bi-info-circle me-2"></i>

            Review and edit the title and abstract for your paper submission. Information has been automatically extracted from your document.
          </p>
        </div>

        {/* Required fields notice */}
        <div className="mb-3">
          <p className="text-muted">
            <span className="text-danger">*</span> Required
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaperTitleAbstract;
